import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { ep } from '../api/endpoints';
import { useAuth } from '../firebase/AuthProvider';
import {
  calculateCurrentFiscalYear,
  MONTH_ORDER,
  type BudgetVersion,
} from '../components/profitAndLoss/constants';

/**
 * 人事採用 計画入力ページのデータ層フック。
 *
 * サーバー (get_recruitment_plan) から支店×部門別の「実績在籍参照 + 保存済み計画フロー値」を
 * 取得し、編集 state (支店×部門×役職×月の採用予定) を保持する。計画在籍数 (ストック) は
 * 保持せず、実績在籍ベースラインへ累積加算してグリッド側で算出する設計。
 * 拠点に「まだ無い部署」を追加する操作 (addDept) はクライアント側の行 (実績在籍0) を足すだけで、
 * 実在籍 (mst_account) には触れない。保存すると計画行として永続化され、再取得で復帰する。
 * 保存 (upsert_recruitment_plan) は全行×12ヶ月のフロー値を一括送信する。
 */

// サーバーから返る支店×部門×役職別の1行 (month_{m}_headcount=実績参照 / _hires / _leaves=保存済み計画)
export type RecruitmentPlanRow = {
  branch: string;
  branch_name: string;
  department: string;
  department_name: string;
  position: string; // 役職区分 (mst_account.position 由来 空は未設定)
  [key: string]: any;
};

// 役職マスタ (mst_position) の1件。役職追加ドロップダウンの候補・並び順の正本
export type PositionMaster = {
  position_code: string;
  position_name: string;
  position_short: string | null;
  category_code: string;
  rank_level: number | null;
  sort_no: number | null;
  is_management: boolean;
  is_active: boolean;
};

// 編集中の計画値 (増員計画のため採用予定のみ)。キーは `${branch}_${department}_${position}_${month}`
export type PlanEdits = Record<string, { hires: number }>;

export const editKey = (branchCode: string, deptCode: string, position: string, month: number) =>
  `${branchCode}_${deptCode}_${position}_${month}`;

// 実績在籍0の空行を生成する (拠点・部門へ役職を新規追加する際の計画専用行)
const makeEmptyRow = (
  branch: string,
  branchName: string,
  dept: string,
  deptName: string,
  position: string,
): RecruitmentPlanRow => {
  const row: RecruitmentPlanRow = {
    branch,
    branch_name: branchName,
    department: dept,
    department_name: deptName,
    position,
  };
  MONTH_ORDER.forEach((m) => {
    row[`month_${m}_headcount`] = 0;
    row[`month_${m}_hires`] = 0;
  });
  return row;
};

export function useRecruitmentPlan() {
  const { user } = useAuth();

  const [fiscalYear, setFiscalYear] = useState(calculateCurrentFiscalYear());
  const [budgetVersions, setBudgetVersions] = useState<BudgetVersion[]>([]);
  const [versionCode, setVersionCode] = useState<string>('');
  // 予算バージョンの初期取得が完了したか (成否問わず)。完了するまで計画取得を待ち二重フェッチを防ぐ
  const [budgetReady, setBudgetReady] = useState(false);

  // 役職マスタ (mst_position)。get_recruitment_plan の応答に同梱される。役職追加の候補・並び順に使う
  const [availablePositions, setAvailablePositions] = useState<PositionMaster[]>([]);

  // サーバー取得の行と、クライアントで追加した部署行を分けて保持する
  const [serverRows, setServerRows] = useState<RecruitmentPlanRow[]>([]);
  const [addedRows, setAddedRows] = useState<RecruitmentPlanRow[]>([]);
  // ユーザーが追加した空の拠点 (まだ部署行が無い拠点)。部署を足すと addedRows 経由で planRows に現れる
  const [addedBranches, setAddedBranches] = useState<{ code: string; name: string }[]>([]);
  const [edits, setEdits] = useState<PlanEdits>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  // グリッド・サマリー・保存が使う実効行 = サーバー行 + 追加部署行
  const planRows = useMemo(() => [...serverRows, ...addedRows], [serverRows, addedRows]);

  // 予算バージョン一覧の取得 (初回のみ)。先頭を初期選択にする
  useEffect(() => {
    const fetchVersions = async () => {
      try {
        const res = await axios.post(ep('get_active_budget_versions'), {});
        if (res.data.status === 'success') {
          const versions: BudgetVersion[] = res.data.data || [];
          setBudgetVersions(versions);
          if (versions.length > 0) setVersionCode(versions[0].version_code);
        }
      } catch (e) {
        console.error('予算バージョン取得エラー:', e);
      } finally {
        setBudgetReady(true);
      }
    };
    fetchVersions();
  }, []);

  // 計画取得 (年度・バージョン変更時)。追加部署行はリセットする
  const fetchPlan = useCallback(async () => {
    if (!versionCode) return;
    setLoading(true);
    setError(null);
    setAddedBranches([]);
    try {
      const res = await axios.post(ep('get_recruitment_plan'), {
        fiscal_year: fiscalYear,
        version_code: versionCode,
      });
      if (res.data.status === 'success') {
        const rows: RecruitmentPlanRow[] = res.data.data || [];
        setServerRows(rows);
        setAvailablePositions(res.data.positions || []);
        setAddedRows([]);
        // 編集 state を保存済み値で初期化
        const initial: PlanEdits = {};
        rows.forEach((r) => {
          MONTH_ORDER.forEach((m) => {
            initial[editKey(r.branch, r.department, r.position, m)] = {
              hires: Number(r[`month_${m}_hires`]) || 0,
            };
          });
        });
        setEdits(initial);
        setDirty(false);
      } else {
        setServerRows([]);
        setAddedRows([]);
        setError('計画の取得に失敗しました。');
      }
    } catch (e) {
      console.error('採用計画の取得エラー:', e);
      setServerRows([]);
      setAddedRows([]);
      setError('計画の取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [fiscalYear, versionCode]);

  useEffect(() => {
    if (!budgetReady) return;
    fetchPlan();
  }, [budgetReady, fetchPlan]);

  // 1セル分の採用予定 (増員) を更新する
  const setEdit = useCallback(
    (branchCode: string, deptCode: string, position: string, month: number, value: number) => {
      const safe = Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
      setEdits((prev) => {
        const key = editKey(branchCode, deptCode, position, month);
        if ((prev[key]?.hires ?? 0) === safe) return prev;
        return { ...prev, [key]: { hires: safe } };
      });
      setDirty(true);
    },
    [],
  );

  // 拠点×部門へ役職区分を新規追加する (実績在籍0の計画行を足す)。既存行と重複する場合は何もしない
  const addPosition = useCallback(
    (branchCode: string, branchName: string, deptCode: string, deptName: string, position: string) => {
      const exists =
        serverRows.some((r) => r.branch === branchCode && r.department === deptCode && r.position === position) ||
        addedRows.some((r) => r.branch === branchCode && r.department === deptCode && r.position === position);
      if (exists) return;
      setAddedRows((prev) => [...prev, makeEmptyRow(branchCode, branchName, deptCode, deptName, position)]);
      setEdits((prev) => {
        const next = { ...prev };
        MONTH_ORDER.forEach((m) => {
          const k = editKey(branchCode, deptCode, position, m);
          if (!next[k]) next[k] = { hires: 0 };
        });
        return next;
      });
      setDirty(true);
    },
    [serverRows, addedRows],
  );

  // 拠点へ部署を新規追加する。新規部署はまず役職「一般」の計画行で立て、以後は addPosition で役職を足す
  const addDept = useCallback(
    (branchCode: string, branchName: string, deptCode: string, deptName: string) => {
      addPosition(branchCode, branchName, deptCode, deptName, '一般');
    },
    [addPosition],
  );

  // 拠点を新規追加する (従業員0でまだ行が無い拠点を計画ツリーへ出す)。部署は addDept で足す。
  // 既に行がある拠点は何もしない。空拠点のままでは保存対象が無いため dirty にはしない。
  const addBranch = useCallback(
    (branchCode: string, branchName: string) => {
      const hasRows =
        serverRows.some((r) => r.branch === branchCode) || addedRows.some((r) => r.branch === branchCode);
      if (hasRows) return;
      setAddedBranches((prev) => (prev.some((b) => b.code === branchCode) ? prev : [...prev, { code: branchCode, name: branchName }]));
    },
    [serverRows, addedRows],
  );

  // 全行×12ヶ月のフロー値を一括 upsert
  const save = useCallback(async (): Promise<boolean> => {
    if (!versionCode) return false;
    setSaving(true);
    try {
      const rows: {
        branch_code: string;
        department_code: string;
        position: string;
        target_month: number;
        planned_hires: number;
      }[] = [];
      planRows.forEach((r) => {
        MONTH_ORDER.forEach((m) => {
          const e = edits[editKey(r.branch, r.department, r.position, m)] || { hires: 0 };
          rows.push({
            branch_code: r.branch,
            department_code: r.department,
            position: r.position,
            target_month: m,
            planned_hires: e.hires,
          });
        });
      });
      const res = await axios.post(ep('upsert_recruitment_plan'), {
        fiscal_year: fiscalYear,
        version_code: versionCode,
        updated_by: user?.email ?? null,
        rows,
      });
      if (res.data.status === 'success') {
        setDirty(false);
        return true;
      }
      return false;
    } catch (e) {
      console.error('採用計画の保存エラー:', e);
      return false;
    } finally {
      setSaving(false);
    }
  }, [planRows, edits, fiscalYear, versionCode, user]);

  return {
    fiscalYear,
    setFiscalYear,
    budgetVersions,
    versionCode,
    setVersionCode,
    planRows,
    availablePositions,
    edits,
    setEdit,
    addDept,
    addPosition,
    addBranch,
    addedBranches,
    loading,
    saving,
    error,
    dirty,
    save,
    reload: fetchPlan,
  };
}

export type UseRecruitmentPlanResult = ReturnType<typeof useRecruitmentPlan>;
